import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Divider,
  Paper,
  ImageList,
  ImageListItem,
  Dialog,
  IconButton
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import HomeIcon from '@mui/icons-material/Home';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TelegramIcon from '@mui/icons-material/Telegram';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PhoneIcon from '@mui/icons-material/Phone';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

// Функция для получения ID видео из URL
const getVideoId = (url, source = 'youtube') => {
  if (!url || typeof url !== 'string') {
    console.warn('getVideoId: URL is empty or not a string');
    return null;
  }

  try {
    let videoId = null;

    // Проверяем, не является ли URL уже встроенным
    if (source === 'youtube') {
      if (url.includes('youtube.com/embed/')) {
        videoId = url.split('/').pop().split('?')[0];
        return videoId;
      }
      
      // YouTube URL patterns
      const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/ ]{11})/i;
      const match = url.match(youtubeRegex);
      if (match && match[1]) {
        videoId = match[1];
      }
    } else if (source === 'vimeo') {
      if (url.includes('player.vimeo.com/video/')) {
        videoId = url.split('/').pop().split('?')[0];
        return videoId;
      }
      
      // Vimeo URL patterns
      const vimeoRegex = /(?:vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/(?:[^\/]*)\/videos\/|album\/(?:\d+)\/video\/|video\/|)(\d+)(?:[a-zA-Z0-9_-]+)?)/i;
      const match = url.match(vimeoRegex);
      if (match && match[1]) {
        videoId = match[1];
      }
    } else if (source === 'rutube') {
      // Проверяем, не является ли URL уже встроенным
      if (url.includes('rutube.ru/play/embed/')) {
        videoId = url.split('/').pop().split('?')[0];
        return videoId;
      }
      
      // Для Rutube shorts с длинным ID
      if (url.includes('rutube.ru/shorts/')) {
        const urlParts = url.split('/');
        // Берем часть после 'shorts/'
        for (let i = 0; i < urlParts.length; i++) {
          if (urlParts[i] === 'shorts' && i + 1 < urlParts.length) {
            // Удаляем слэш в конце, если он есть
            videoId = urlParts[i + 1].replace(/\/$/, '');
            console.log(`Получен ID для Rutube shorts: ${videoId}`);
            return videoId;
          }
        }
      }
      
      // Для обычных видео Rutube
      if (url.includes('rutube.ru/video/')) {
        const urlParts = url.split('/');
        for (let i = 0; i < urlParts.length; i++) {
          if (urlParts[i] === 'video' && i + 1 < urlParts.length) {
            videoId = urlParts[i + 1].replace(/\/$/, '');
            console.log(`Получен ID для Rutube video: ${videoId}`);
            return videoId;
          }
        }
      }
      
      // Если предыдущие методы не сработали, пробуем регулярное выражение
      const rutubeRegex = /(?:rutube\.ru\/(?:video\/|shorts\/|play\/embed\/)([a-zA-Z0-9]+))/i;
      const match = url.match(rutubeRegex);
      if (match && match[1]) {
        videoId = match[1];
        console.log(`Получен ID по регулярке: ${videoId}`);
        return videoId;
      }
      
      // Если все методы не сработали, пробуем извлечь ID из последней части URL
      const urlParts = url.split('/');
      // Берем последнюю часть URL после последнего слэша
      const lastPart = urlParts[urlParts.length - 1];
      if (lastPart && lastPart.length > 0) {
        // Удаляем слэш в конце, если он есть
        videoId = lastPart.replace(/\/$/, '');
        console.log(`Получен ID из последней части URL: ${videoId}`);
      }
    }

    return videoId;
  } catch (error) {
    console.error('Ошибка при получении ID видео:', error);
    return null;
  }
};

// Функция для получения URL для встраивания видео
const getEmbedUrl = (url, source = 'youtube') => {
  // Если URL пустой или не строка
  if (!url || typeof url !== 'string') {
    console.warn('getEmbedUrl: URL пустой или не является строкой:', url);
    return null;
  }
  
  try {
    // Если URL уже является встроенным, вернуть его как есть
    if (url.includes('youtube.com/embed/') || 
        url.includes('player.vimeo.com/video/') || 
        url.includes('rutube.ru/play/embed/')) {
      return url;
    }

    // Прямая обработка Rutube shorts URL
    if (url.includes('rutube.ru/shorts/')) {
      const urlParts = url.split('/');
      for (let i = 0; i < urlParts.length; i++) {
        if (urlParts[i] === 'shorts' && i + 1 < urlParts.length) {
          const videoId = urlParts[i + 1].replace(/\/$/, '');
          console.log(`Получен ID для Rutube shorts напрямую: ${videoId}`);
          return `https://rutube.ru/play/embed/${videoId}`;
        }
      }
    }

    // Получаем ID видео
    const videoId = getVideoId(url, source);
    if (!videoId) {
      console.warn(`getEmbedUrl: Не удалось получить ID видео из URL: ${url}`);
      return null;
    }

    // Формируем URL для встраивания в зависимости от источника
    let embedUrl;
    if (source === 'youtube') {
      embedUrl = `https://www.youtube.com/embed/${videoId}`;
    } else if (source === 'vimeo') {
      embedUrl = `https://player.vimeo.com/video/${videoId}`;
    } else if (source === 'rutube') {
      embedUrl = `https://rutube.ru/play/embed/${videoId}`;
    } else {
      console.warn(`getEmbedUrl: Неизвестный источник видео: ${source}`);
      return null;
    }

    console.log(`Создан URL для встраивания: ${embedUrl}`);
    return embedUrl;
  } catch (error) {
    console.error('Ошибка при получении URL для встраивания видео:', error);
    return null;
  }
};

// Компонент для отображения видео
const VideoCard = ({ video }) => {
  const [thumbnailLoaded, setThumbnailLoaded] = useState(false);

  // Обработчик загрузки превью
  const handleThumbnailLoad = () => {
    setThumbnailLoaded(true);
  };

  // Проверяем наличие URL для видео и превью
  const videoUrl = video.url || video.videoUrl || '';
  const embedUrl = video.embedUrl || getEmbedUrl(videoUrl, video.source);
  const hasEmbedUrl = embedUrl && embedUrl.trim() !== '';
  const hasThumbnail = video.thumbnailUrl || video.thumbnail;
  
  // Используем заглушку, если нет превью
  const thumbnailImage = hasThumbnail ? (video.thumbnailUrl || video.thumbnail) : '/assets/placeholders/no-video-preview.jpg';
  
  // Отладочная информация
  useEffect(() => {
    if (hasEmbedUrl) {
      console.log(`Загрузка видео "${video.title || video.caption || 'Без названия'}" (ID: ${video.id || 'новое'}):`, {
        url: videoUrl,
        embedUrl: embedUrl,
        source: video.source
      });
    }
  }, [video.id, video.title, video.caption, videoUrl, embedUrl, video.source, hasEmbedUrl]);
  
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ position: 'relative', paddingTop: '56.25%' }}>
        {hasEmbedUrl ? (
          <Box
            component="iframe"
            src={embedUrl}
            title={video.title || video.caption || 'Видео'}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              border: 0
            }}
            onError={(e) => console.error('Ошибка загрузки iframe:', e)}
          />
        ) : (
          <>
            <CardMedia
              component="img"
              image={thumbnailImage}
              alt={video.title || video.caption || 'Видео'}
              onLoad={handleThumbnailLoad}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: thumbnailLoaded ? 'block' : 'none'
              }}
            />
            {!thumbnailLoaded && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#f0f0f0'
                }}
              >
                <CircularProgress />
              </Box>
            )}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.2)'
              }}
            >
              {videoUrl ? (
                <PlayArrowIcon sx={{ fontSize: 64, color: 'white' }} />
              ) : (
                <Typography variant="body1" color="white">
                  Видео недоступно
                </Typography>
              )}
            </Box>
          </>
        )}
      </Box>
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography gutterBottom variant="h6" component="div">
          {video.title || video.caption || 'Видео'}
        </Typography>
        {video.description && (
          <Typography variant="body2" color="text.secondary">
            {video.description.length > 100
              ? `${video.description.substring(0, 100)}...`
              : video.description}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const ServiceDetails = () => {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [phone, setPhone] = useState('');
  const [contactsLoading, setContactsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Image gallery state
  const [openGallery, setOpenGallery] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Загрузка контактной информации
  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        const response = await axios.get('/api/settings');
        if (response.data && response.data.contactPhone) {
          // Remove all non-digit characters for links
          const cleanPhone = response.data.contactPhone.replace(/\D/g, '');
          setPhone(cleanPhone);
        }
      } catch (error) {
        console.error('Error fetching contact info:', error);
      } finally {
        setContactsLoading(false);
      }
    };

    fetchContactInfo();
  }, []);

  useEffect(() => {
    const fetchServiceDetails = async () => {
      try {
        setLoading(true);
        
        // Fetch service details
        const serviceResponse = await axios.get(`/api/services/${slug}`);
        setService(serviceResponse.data);
        
        // Fetch service images
        const imagesResponse = await axios.get(`/api/services/${serviceResponse.data.id}/images`);
        setImages(imagesResponse.data);
        
        // Fetch service videos
        const videosResponse = await axios.get(`/api/services/${serviceResponse.data.id}/videos`);
        setVideos(videosResponse.data);
        
        // Fetch custom prices
        const customPricesResponse = await axios.get(`/api/services/${serviceResponse.data.id}/custom-prices`);
        // Добавляем кастомные цены к объекту услуги
        setService(prev => ({
          ...prev,
          ServiceCustomPrices: customPricesResponse.data
        }));
        console.log('Загружены кастомные цены:', customPricesResponse.data);
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching service details:', err);
        setError('Не удалось загрузить информацию об услуге. Пожалуйста, попробуйте позже.');
        setLoading(false);
      }
    };
    
    fetchServiceDetails();
  }, [slug]);
  
  // Format price based on price type
  const formatPrice = (service) => {
    if (!service) return '';
    
    switch (service.priceType) {
      case 'fixed':
        return service.basePrice ? `ОТ ${Math.floor(service.basePrice)} ₽` : 'Цена по запросу';
      case 'hourly':
        return service.hourlyPrice ? `ОТ ${Math.floor(service.hourlyPrice)} ₽/час` : 'Цена по запросу';
      case 'daily':
        return service.dailyPrice ? `ОТ ${Math.floor(service.dailyPrice)} ₽/день` : 'Цена по запросу';
      default:
        return service.basePrice ? `ОТ ${Math.floor(service.basePrice)} ₽` : 'Цена по запросу';
    }
  };
  
  // Get price type label
  const getPriceTypeLabel = (priceType) => {
    switch (priceType) {
      case 'fixed':
        return 'Фиксированная цена';
      case 'hourly':
        return 'Почасовая оплата';
      case 'daily':
        return 'Оплата за день';
      default:
        return '';
    }
  };
  
  // Check if service has custom prices
  const hasCustomPrices = (service) => {
    return service.ServiceCustomPrices && service.ServiceCustomPrices.length > 0;
  };
  
  // Функции обработки контактов объявлены ниже
  
  // Open image gallery
  const handleOpenGallery = (index) => {
    setCurrentImageIndex(index);
    setOpenGallery(true);
  };
  
  // Close image gallery
  const handleCloseGallery = () => {
    setOpenGallery(false);
  };
  
  // Navigate to next image
  const handleNextImage = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % (images.length + 1)); // +1 for featured image
  };
  
  // Navigate to previous image
  const handlePrevImage = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex - 1 + images.length + 1) % (images.length + 1)); // +1 for featured image
  };
  
  // Функция для нормализации URL изображения
  const getFullImageUrl = (url) => {
    if (!url) {
      return '/images/placeholder-service.jpg';
    }
    // Абсолютные URL и data: оставляем как есть
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
      return url;
    }
    // Если уже начинается с "/" (например, /images/... или /uploads/...), используем как есть
    if (url.startsWith('/')) {
      return url;
    }
    // Для относительных путей без ведущего слеша добавляем его
    return `/${url}`;
  };
  
  // Get all images including featured image
  const getAllImages = () => {
    if (!service) return [];
    
    console.log('Данные услуги для галереи:', service);
    console.log('Дополнительные изображения:', images);
    
    const allImages = [];
    
    // Добавляем главное изображение, если оно есть
    if (service.featuredImage) {
      console.log('Добавляем главное изображение:', service.featuredImage);
      allImages.push({ id: 'featured', imageUrl: getFullImageUrl(service.featuredImage), caption: service.name });
    }
    
    // Преобразуем URL всех дополнительных изображений и сохраняем их подписи
    const processedImages = images.map(image => ({
      id: image.id,
      imageUrl: getFullImageUrl(image.imageUrl),
      caption: image.caption || '' // Убедимся, что подпись всегда определена
    }));
    
    console.log('Обработанные изображения с подписями:', processedImages);
    
    return [...allImages, ...processedImages];
  };
  
  // Extract YouTube video ID from URL
  const getYoutubeVideoId = (url) => {
    if (!url) return null;
    
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    
    return (match && match[2].length === 11) ? match[2] : null;
  };
  
  // Contact functions
  const handleContactTelegram = () => {
    window.open('https://t.me/+79630600255', '_blank');
  };
  
  const handleContactWhatsApp = () => {
    if (phone) {
      window.open(`https://wa.me/${phone}`, '_blank');
    }
  };
  
  const handleContactPhone = () => {
    if (phone) {
      window.open(`tel:+${phone}`, '_blank');
    }
  };
  
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs 
        separator={<NavigateNextIcon fontSize="small" />} 
        aria-label="breadcrumb"
        sx={{ mb: 3 }}
      >
        <Link
          underline="hover"
          color="inherit"
          component={RouterLink}
          to="/"
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Главная
        </Link>
        <Link
          underline="hover"
          color="inherit"
          component={RouterLink}
          to="/services"
        >
          Услуги
        </Link>
        {service && (
          <Typography color="text.primary">{service.name}</Typography>
        )}
      </Breadcrumbs>
      
      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}
      
      {/* Loading indicator */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : service ? (
        <>
          {/* Service details */}
          <Grid container spacing={4}>
            {/* Main content */}
            <Grid item xs={12} md={8}>
              {/* Featured image */}
              <Card sx={{ mb: 4 }}>
                <CardMedia
                  component="img"
                  height="400"
                  image={getFullImageUrl(service.featuredImage)}
                  alt={service.name}
                  onError={(e) => {
                    console.error('Ошибка загрузки изображения:', e);
                    e.target.src = '/images/placeholder-service.jpg';
                  }}
                  sx={{ 
                    objectFit: 'cover',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleOpenGallery(0)}
                />
              </Card>
              
              {/* Service name and description */}
              <Typography variant="h4" component="h1" gutterBottom>
                {service.name}
              </Typography>
              
              {/* Price information */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Chip 
                  label={formatPrice(service)} 
                  color="primary" 
                  size="medium"
                  sx={{ mr: 2, fontWeight: 'bold', fontSize: '1rem' }}
                />
                <Typography variant="body1" color="text.secondary">
                  {getPriceTypeLabel(service.priceType)}
                </Typography>
              </Box>
              
              {/* Main description with better formatting */}
              <Paper elevation={2} sx={{ p: 3, mb: 4, bgcolor: 'background.paper', borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'medium' }}>
                  Описание услуги
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {service.description.split('\n').map((paragraph, index) => (
                  <Typography key={index} variant="body1" paragraph>
                    {paragraph}
                  </Typography>
                ))}
              </Paper>
              
              {/* Additional details with better formatting */}
              {(() => {
                // Проверяем, есть ли содержательные данные в details
                let hasContent = false;
                let detailsText = '';
                
                if (typeof service.details === 'string' && service.details.trim() !== '') {
                  hasContent = true;
                  detailsText = service.details;
                } else if (service.details && service.details.text && service.details.text.trim() !== '') {
                  hasContent = true;
                  detailsText = service.details.text;
                } else if (service.details && Object.keys(service.details).length > 0 && 
                          JSON.stringify(service.details) !== '{}') {
                  hasContent = true;
                  detailsText = JSON.stringify(service.details);
                }
                
                // Если есть содержимое, отображаем раздел
                if (hasContent) {
                  return (
                    <Paper elevation={2} sx={{ p: 3, mb: 4, bgcolor: 'background.paper', borderRadius: 2 }}>
                      <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'medium' }}>
                        Дополнительная информация
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      {detailsText.split('\n').map((paragraph, index) => (
                        <Typography key={index} variant="body1" paragraph>
                          {paragraph}
                        </Typography>
                      ))}
                    </Paper>
                  );
                }
                
                // Если нет содержимого, не отображаем раздел
                return null;
              })()}
              
              {/* Additional images */}
              {images.length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                    Галерея
                  </Typography>
                  <ImageList cols={3} gap={16}>
                    {images.map((image, index) => (
                      <ImageListItem 
                        key={image.id}
                        onClick={() => handleOpenGallery(index + 1)} // +1 because featured image is at index 0
                        sx={{ cursor: 'pointer' }}
                      >
                        <img
                          src={image.imageUrl}
                          alt={image.caption || `Изображение ${index + 1}`}
                          loading="lazy"
                          style={{ borderRadius: 4 }}
                        />
                        {image.caption && (
                          <Box sx={{ 
                            p: 1, 
                            bgcolor: 'rgba(0, 0, 0, 0.7)', 
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            maxHeight: '40%',
                            overflow: 'hidden'
                          }}>
                            <Typography variant="caption" component="div" sx={{ fontWeight: 'medium', color: 'white' }}>
                              {image.caption}
                            </Typography>
                          </Box>
                        )}
                      </ImageListItem>
                    ))}
                  </ImageList>
                </>
              )}
              
              {/* Раздел с видео */}
              {videos && videos.length > 0 && (
                <Paper elevation={2} sx={{ p: 3, mb: 4, bgcolor: 'background.paper', borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'medium' }}>
                    Видео ({videos.length})
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Grid container spacing={2}>
                    {videos.map((serviceVideo, index) => {
                      // Получаем данные видео
                      const videoUrl = serviceVideo.videoUrl || '';
                      const caption = serviceVideo.caption || `Видео ${index + 1}`;
                      
                      if (!videoUrl) {
                        console.log(`Видео ${index} не имеет URL`);
                        return null;
                      }
                      
                      console.log(`Видео ${index}:`, { videoUrl, caption });
                      
                      return (
                        <Grid item xs={12} sm={6} md={4} key={`video-${serviceVideo.id || index}`}>
                          <VideoCard video={{ 
                            id: serviceVideo.id || index,
                            videoUrl: videoUrl,
                            thumbnail: serviceVideo.thumbnail,
                            title: caption,
                            description: serviceVideo.description || ''
                          }} />
                        </Grid>
                      );
                    })}
                  </Grid>
                </Paper>
              )}
            </Grid>
            
            {/* Sidebar */}
            <Grid item xs={12} md={4}>
              {/* Price information */}
              <Paper elevation={3} sx={{ p: 3, mb: 4, position: 'sticky', top: 20, zIndex: 10, bgcolor: '#1a1a1a', color: 'white', borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ borderBottom: '1px solid #333', pb: 1, fontWeight: 500 }}>
                  Стоимость
                </Typography>
                
                {/* Main price */}
                <Box sx={{ mb: 3, p: 2, bgcolor: '#222', borderRadius: 1, boxShadow: '0 4px 8px rgba(0,0,0,0.2)' }}>
                  <Typography variant="h5" color="#64b5f6" align="center" gutterBottom sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
                    {formatPrice(service)}
                  </Typography>
                  <Typography variant="body2" color="#aaa" align="center">
                    {getPriceTypeLabel(service.priceType)}
                  </Typography>
                </Box>
                
                {/* Custom prices */}
                {hasCustomPrices(service) && (
                  <>
                    <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, mb: 2, fontWeight: 500 }}>
                      Дополнительные услуги и цены:
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      {service.ServiceCustomPrices.map((price) => (
                        <Box 
                          key={price.id} 
                          sx={{ 
                            mb: 2, 
                            p: 1.5, 
                            bgcolor: '#222', 
                            borderRadius: 1,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                            '&:hover': { bgcolor: '#2a2a2a' }
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {price.name}
                            </Typography>
                            <Typography variant="body1" fontWeight="bold" color="#64b5f6" sx={{ ml: 2, whiteSpace: 'nowrap' }}>
                            {Math.floor(price.price)} ₽
                            </Typography>
                          </Box>
                          {price.description && (
                            <Typography variant="body2" color="#aaa" sx={{ mt: 0.5 }}>
                              {price.description}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </>
                )}
                
                <Divider sx={{ my: 2 }} />
                
                {/* Contact information */}
                <Typography variant="h6" gutterBottom>
                  Связаться с нами
                </Typography>
                <List>
                  <ListItem disableGutters>
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<TelegramIcon />}
                      onClick={handleContactTelegram}
                      sx={{ justifyContent: 'flex-start', mb: 1 }}
                    >
                      Telegram
                    </Button>
                  </ListItem>
                  <ListItem disableGutters>
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<WhatsAppIcon />}
                      onClick={handleContactWhatsApp}
                      sx={{ justifyContent: 'flex-start', mb: 1 }}
                    >
                      WhatsApp
                    </Button>
                  </ListItem>
                  <ListItem disableGutters>
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<PhoneIcon />}
                      onClick={handleContactPhone}
                      sx={{ justifyContent: 'flex-start' }}
                    >
                      Позвонить
                    </Button>
                  </ListItem>
                </List>
              </Paper>
              
              {/* Back to services */}
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                component={RouterLink}
                to="/services"
                fullWidth
              >
                Вернуться к списку услуг
              </Button>
            </Grid>
          </Grid>
          
          {/* Image Gallery Dialog */}
          <Dialog
            open={openGallery}
            onClose={handleCloseGallery}
            maxWidth="lg"
            fullWidth
          >
            <Box sx={{ position: 'relative' }}>
              <IconButton
                aria-label="close"
                onClick={handleCloseGallery}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                  color: 'white',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  }
                }}
              >
                <CloseIcon />
              </IconButton>
              
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                backgroundColor: 'black',
                position: 'relative'
              }}>
                <img
                  src={getAllImages()[currentImageIndex]?.imageUrl}
                  alt={getAllImages()[currentImageIndex]?.caption || 'Изображение'}
                  style={{ maxWidth: '100%', maxHeight: '80vh' }}
                />
                
                <IconButton
                  aria-label="previous"
                  onClick={handlePrevImage}
                  sx={{
                    position: 'absolute',
                    left: 8,
                    color: 'white',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    }
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
                
                <IconButton
                  aria-label="next"
                  onClick={handleNextImage}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    color: 'white',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    }
                  }}
                >
                  <ArrowForwardIcon />
                </IconButton>
              </Box>
              
              {getAllImages()[currentImageIndex]?.caption && (
                <Box sx={{ p: 2, backgroundColor: 'black', color: 'white' }}>
                  <Typography variant="body1">
                    {getAllImages()[currentImageIndex]?.caption}
                  </Typography>
                </Box>
              )}
            </Box>
          </Dialog>
        </>
      ) : (
        <Alert severity="warning">
          Услуга не найдена
        </Alert>
      )}
    </Container>
  );
};

export default ServiceDetails;
